import { generateId } from '@ai-sdk/provider-utils';
import { useEffect, useState } from 'react';
import { Alert } from '~/components/ui/Alert';
import { Button, DefaultLoader } from '~/components/ui/Button';
import * as Tooltip from '~/components/ui/Tooltip';
import { chatHooks } from '~/lib/chat-hooks';
import { BOLT_FOLDER } from '~/lib/constants';
import { workbenchStore } from '~/lib/stores/workbench';
import { webcontainer as webcontainerPromise } from '~/lib/webcontainer';
import type { Message } from '~/types/chat';
import { plural } from '~/utils/plural';
import { createSnapshot } from '~/utils/snapshots';

interface Props {
  isLast: boolean;
  messageId: Message['id'];
  onUndo: () => void;
  className?: string;
  defaultState?: State;
  tooltipOpen?: boolean;
}

type State =
  | { type: 'IDLE' }
  | { type: 'CLEANING' }
  | { type: 'DONE'; initialFileCount: number; removedFiles: string[] | null };

export function LargeProjectError({
  isLast,
  onUndo,
  messageId,
  className,
  defaultState = { type: 'IDLE' },
  tooltipOpen = false,
}: Props) {
  const [state, setState] = useState<State>(defaultState);

  // reset back to initial state when rollbacked
  useEffect(() => {
    return workbenchStore.lastRollbackedMessageId.subscribe((id) => {
      if (id === messageId) {
        setState({ type: 'IDLE' });
      }
    });
  }, [messageId]);

  if (state.type === 'DONE') {
    const { initialFileCount, removedFiles } = state;

    if (removedFiles === null) {
      return (
        <Alert
          title="Cleanup didn't go as planned"
          type="warning"
          actions={<DocumentationLink />}
          className={className}
        >
          We couldn't remove anything automatically. You can still clean things up manually if needed.
        </Alert>
      );
    }

    const removedFileCount = removedFiles.length;

    if (removedFileCount === 0) {
      return (
        <Alert title="No unused files found" type="success" actions={<DocumentationLink />} className={className}>
          Your project is as lean as it gets. To keep building, try one of the other solutions below.
        </Alert>
      );
    }

    const percentage = Math.round((removedFileCount / initialFileCount) * 100);
    const formattedPercentage = percentage > 0 && percentage !== Infinity ? `${percentage}% ` : '';

    const fileLimit = 10;
    const limitFiles = removedFileCount > fileLimit;

    return (
      <Alert
        title="Project cleaned"
        type="success"
        actions={
          <Button size="sm" variant="secondary" startIcon="i-ph:clock-counter-clockwise" onClick={onUndo}>
            Undo
          </Button>
        }
        className={className}
      >
        <Tooltip.Provider>
          <Tooltip.Root defaultOpen={tooltipOpen} delayDuration={300}>
            <Tooltip.Trigger asChild>
              <ins className="cursor-pointer">
                {removedFileCount} {plural(removedFileCount, 'file')} removed
              </ins>
            </Tooltip.Trigger>
            , making your project {formattedPercentage}lighter.
            <Tooltip.Portal>
              <Tooltip.Content side="right" align="start" sideOffset={4}>
                <ul className="list-disc mx-5 my-1">
                  {removedFiles.slice(0, fileLimit).map((file) => (
                    <li key={file}>{file}</li>
                  ))}

                  {limitFiles && <li>And {removedFileCount - 10} more files</li>}
                </ul>
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        </Tooltip.Provider>
      </Alert>
    );
  }

  return (
    <Alert
      title="Project size exceeded"
      type="info"
      className={className}
      actions={
        <>
          <Button
            size="sm"
            disabled={!isLast || state.type === 'CLEANING'}
            className="flex gap-1 py-0.5"
            onClick={() => onRemoveUsedFiles({ setState, messageId })}
          >
            {state.type === 'CLEANING' && (
              <span className="flex items-center">
                <DefaultLoader />
                <span className="mx-2">Cleaning</span>
              </span>
            )}

            {state.type !== 'CLEANING' && 'Remove unused files'}
          </Button>

          <DocumentationLink />
        </>
      }
    >
      Looks like you've hit the size limit. Try cleaning up some unused files to keep things running smoothly.
    </Alert>
  );
}

function DocumentationLink() {
  return (
    <Button asChild size="sm" variant="secondary">
      <a target="_blank" href="https://bolt.fyi/prompt-too-long">
        <span className="i-ph:arrow-square-out h-5 text-xl leading-5 flex items-center justify-center" />
        Learn more
      </a>
    </Button>
  );
}

async function onRemoveUsedFiles({
  setState,
  messageId,
}: Pick<Props, 'messageId'> & { setState: (state: State) => void }) {
  const initialFileCount = Object.keys(workbenchStore.files.value).length;
  const snapshotSuccesful = createSnapshot([], messageId);

  if (!snapshotSuccesful) {
    setState({ type: 'DONE', initialFileCount, removedFiles: null });
    return;
  }

  setState({ type: 'CLEANING' });

  // do not pass WORK_DIR here. Knip doesn't work with absolute paths as config filenames
  const configFile = `${BOLT_FOLDER}/project-cleanup.json`;

  await workbenchStore.writeFile(configFile, {
    type: 'file',
    content: JSON.stringify({ ignore: ['supabase/**'] }),
  });

  const knipOutput = await runCommand('npx', [
    '--yes',

    // last version without native binaries, faster to download
    'knip@5.55.1',

    // check only files, no linting or deps checks
    '--include',
    'files',

    // exclude configs and tests
    '--production',

    // automatically remove unused files
    '--fix',
    '--allow-remove-files',

    // output formatted JSON report
    '--reporter',
    'json',

    // make output silent
    '--no-progress',

    // some options are not supported as CLI arguments, use custom config file for those
    '--config',
    configFile,
  ]);

  void workbenchStore.removeFile(configFile);

  const removedFiles = parseKnipOutput(knipOutput);

  setState({ type: 'DONE', initialFileCount, removedFiles });

  if (removedFiles !== null) {
    await updateMessages(removedFiles);
  }
}

async function runCommand(command: string, args: string[]) {
  const webcontainer = await webcontainerPromise;

  const process = await webcontainer.spawn(command, args, {
    env: {
      NODE_NO_WARNINGS: '1',
      NO_COLOR: '1',
      CI: '1',
    },
  });

  let output = '';

  process.output.pipeTo(
    new WritableStream({
      write(data) {
        output += data;
      },
    }),
  );

  await process.exit;

  return output;
}

function parseKnipOutput(output: string) {
  try {
    const files: string[] = JSON.parse(output).files;

    if (!Array.isArray(files)) {
      throw new Error('Files is not an array');
    }

    return files;
  } catch {
    console.error('Failed to parse knip output', { output });
    return null;
  }
}

async function updateMessages(removedFiles: string[]) {
  const content =
    removedFiles.length > 0
      ? 'Your project has been cleaned, you should be able to continue building.'
      : [
          "Even though your project is already optimized, it's now too big to handle. ",
          'Try using a <code>.bolt/ignore</code> file or splitting your project into smaller parts. ',
          "Need help? You'll find all the steps below.",
          '\n',
          '<bolt-quick-actions>',
          '<bolt-quick-action type="link" href="https://bolt.fyi/prompt-too-long">How to reduce my project size?</bolt-quick-action>',
          '</bolt-quick-actions>',
        ].join('');

  chatHooks.setMessages((messages) => [
    ...messages,
    {
      role: 'assistant',
      type: 'chat',
      id: generateId(),
      status: 'complete',
      ignore: true,
      allowUndo: removedFiles.length > 0,
      parts: [],
      content,
    },
  ]);

  await workbenchStore.saveAllFiles();
}
